import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, CalendarClock, Search, Filter } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function ArticleManager() {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [catMap, setCatMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const load = async () => {
    try {
      const [artRes, catRes] = await Promise.all([
        api.get("/articles?include_unpublished=true&limit=200"),
        api.get("/categories"),
      ]);
      setArticles(artRes.data.articles || []);
      setCategories(catRes.data || []);
      const map = {};
      (catRes.data || []).forEach(c => { map[c.id] = c; });
      setCatMap(map);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    try {
      await api.delete(`/articles/${id}`);
      toast.success("Article deleted");
      load();
    } catch { toast.error("Failed to delete"); }
  };

  const filtered = articles.filter(art => {
    if (filterCat !== "all" && art.category_id !== filterCat) return false;
    if (searchTerm && !art.title.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  return (
    <div data-testid="article-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-serif font-bold text-foreground">Articles</h1>
          <p className="text-sm font-sans text-muted-foreground mt-1">{articles.length} total &middot; {filtered.length} shown</p>
        </div>
        <Link to="/admin/articles/new">
          <Button className="bg-[#1E88E5] hover:bg-[#1565C0] font-sans" data-testid="new-article-btn">
            <Plus className="h-4 w-4 mr-2" /> New Article
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search articles by title..."
            className="pl-9 h-10"
            data-testid="article-search"
          />
        </div>
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="w-full sm:w-[250px] h-10" data-testid="article-filter-category">
            <Filter className="h-4 w-4 mr-2 text-muted-foreground" />
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(c => (
              <SelectItem key={c.id} value={c.id}>
                <span className="flex items-center gap-2">
                  {c.color && <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: c.color }} />}
                  {c.name}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="font-sans">Title</TableHead>
              <TableHead className="font-sans">Category</TableHead>
              <TableHead className="font-sans">Author</TableHead>
              <TableHead className="font-sans">Status</TableHead>
              <TableHead className="font-sans text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((art) => {
              const cat = catMap[art.category_id];
              return (
                <TableRow key={art.id}>
                  <TableCell className="font-sans font-medium max-w-xs">
                    <span className="line-clamp-1">{art.title}</span>
                  </TableCell>
                  <TableCell>
                    {cat ? (
                      <Badge variant="outline" className="text-[11px] font-sans whitespace-nowrap" style={{ borderColor: cat.color, color: cat.color }}>
                        {cat.name}
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground font-sans">-</span>
                    )}
                  </TableCell>
                  <TableCell className="font-sans text-sm text-muted-foreground whitespace-nowrap">{art.author_name || "-"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Badge variant={art.is_published ? "default" : "secondary"} className={`text-[10px] font-sans ${art.is_published ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" : ""}`}>
                        {art.is_published ? "Published" : "Draft"}
                      </Badge>
                      {art.is_featured && <Badge className="bg-[#1E88E5] text-white text-[10px] font-sans">Featured</Badge>}
                      {art.scheduled_at && new Date(art.scheduled_at) > new Date() && (
                        <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 text-[10px] font-sans border-none">
                          <CalendarClock className="h-3 w-3 mr-1" />
                          {new Date(art.scheduled_at).toLocaleDateString()}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Link to={`/admin/articles/${art.id}/edit`}>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`edit-article-${art.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </Link>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700" onClick={() => handleDelete(art.id)} data-testid={`delete-article-${art.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {filtered.length === 0 && !loading && (
          <p className="text-center py-12 text-muted-foreground font-sans text-sm">
            {searchTerm || filterCat !== "all" ? "No articles match your filters" : "No articles yet"}
          </p>
        )}
      </div>
    </div>
  );
}
